import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, MapPin } from "lucide-react";
import Copyright from "@/components/Copyright";
import BrandLogo from "@/components/BrandLogo";
import PinPad from "@/components/PinPad";
import ForgotPassword from "@/components/ForgotPassword";

const HERO = "https://images.unsplash.com/photo-1574720187210-421b34c9cf01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxzY2hvb2wlMjBidXMlMjByb2FkfGVufDB8fHx8MTc4ODA5NjE3Nnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("pin");          // les chauffeurs sont les plus nombreux
  const [email, setEmail] = useState("s.ledoux@innova-solution.ca");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "driver" ? "/driver" : "/dashboard");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden bg-primary">
        <img src={HERO} alt="Autobus scolaire" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white flex items-center justify-center px-4 py-2.5 shadow-sm">
              <BrandLogo platform size={44} fallbackClassName="w-10 h-10 !bg-transparent" />
            </div>
          </div>
          <div>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight max-w-md">
              Des routes scolaires plus sûres, optimisées et vérifiables.
            </h2>
            <div className="mt-6 space-y-3 text-white/80 text-sm max-w-md">
              <p className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> La sécurité prime toujours sur le temps de parcours.</p>
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-amber-400" /> Ramassage côté droit garanti sur les routes numérotées.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center p-8 bg-background relative">
        {mode === "pin" ? (
          <div className="w-full flex flex-col items-center animate-fade-up">
            <div className="lg:hidden mb-8"><BrandLogo platform size={40} fallbackClassName="w-10 h-10" /></div>
            <PinPad onBack={() => setMode("password")} />
          </div>
        ) : mode === "forgot" ? (
          <ForgotPassword email={email} onBack={() => setMode("password")} />
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <BrandLogo platform size={36} fallbackClassName="w-9 h-9" />
            <span className="font-display text-lg font-extrabold">Innova Route</span>
          </div>
          <div className="overline mb-2">Accès répartiteur / chauffeur</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-8">Connexion</h1>

          {err && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{err}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Courriel</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5" data-testid="login-email" required />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5" data-testid="login-password" required />
            </div>
          </div>

          <Button type="submit" className="w-full mt-6 h-11" disabled={loading} data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
          </Button>

          <div className="mt-6 text-xs text-muted-foreground border-t border-border pt-4">
            Accès réservé aux employés autorisés.
            <button type="button" onClick={() => setMode("forgot")} data-testid="go-forgot"
              className="ml-1 font-semibold text-primary hover:underline">
              Mot de passe oublié ?
            </button>
          </div>
          <button type="button" onClick={() => setMode("pin")} data-testid="go-pin-login"
            className="mt-4 w-full text-sm font-semibold text-primary hover:underline">
            Je suis chauffeur — connexion par NIP
          </button>
        </form>
        )}
        <div className="absolute bottom-0 left-0 right-0 max-w-sm mx-auto">
          <Copyright />
        </div>
      </div>
    </div>
  );
}
